import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import TextDecreaseIcon from '@mui/icons-material/TextDecrease'
import TextIncreaseIcon from '@mui/icons-material/TextIncrease'
import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material'
import React from 'react'

export const MIN_FONT = 12
export const MAX_FONT = 44

const TEXT_COLORS = ['#000000', '#d32f2f', '#ed6c02', '#2e7d32', '#1976d2']

type Props = {
  fontSize: number
  onFontSize: (n: number) => void
  // Extra control rendered at the end of the row (e.g. autoscroll).
  trailing?: React.ReactNode
}

// onMouseDown + preventDefault keeps the selection in the focused editable text,
// so the command applies there even though the toolbar lives up here.
const exec = (command: string, arg?: string) => (e: React.MouseEvent) => {
  e.preventDefault()
  document.execCommand(command, false, arg)
}

// Text controls above the reading text: font size + inline formatting (applied
// to the currently selected text in the editable areas). Width is set by the
// reader's drag edges, not here.
export const ReadingControls: React.FC<Props> = ({
  fontSize,
  onFontSize,
  trailing
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 1,
      flex: 0
    }}
  >
    <Typography variant="caption" color="text.secondary">
      Schrift
    </Typography>
    <Tooltip title="Schrift kleiner">
      <span>
        <IconButton
          size="small"
          disabled={fontSize <= MIN_FONT}
          onClick={() => onFontSize(Math.max(MIN_FONT, fontSize - 2))}
        >
          <TextDecreaseIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
    <Typography variant="caption" sx={{ width: 28, textAlign: 'center' }}>
      {fontSize}
    </Typography>
    <Tooltip title="Schrift größer">
      <span>
        <IconButton
          size="small"
          disabled={fontSize >= MAX_FONT}
          onClick={() => onFontSize(Math.min(MAX_FONT, fontSize + 2))}
        >
          <TextIncreaseIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>

    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

    <Tooltip title="Fett (markierten Text)">
      <IconButton size="small" onMouseDown={exec('bold')}>
        <FormatBoldIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Kursiv (markierten Text)">
      <IconButton size="small" onMouseDown={exec('italic')}>
        <FormatItalicIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
      {TEXT_COLORS.map((color) => (
        <Tooltip key={color} title="Textfarbe">
          <Box
            onMouseDown={exec('foreColor', color)}
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: color,
              cursor: 'pointer',
              border: '1px solid',
              borderColor: 'divider'
            }}
          />
        </Tooltip>
      ))}
    </Box>

    {trailing ? <Box sx={{ ml: 1 }}>{trailing}</Box> : null}
  </Box>
)
